import { HOMEWORK_DEFAULT_TITLE_KEYS } from '@cc/schema';
import { useCustom, useTranslate } from '@refinedev/core';
import { App as AntdApp, Button, Grid, Space, Table, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import {
  EmptyState,
  ResultCard,
  ToolAssignButton,
  ToolSection,
  dayOf,
  fmtDay,
  fmtWeekday,
} from '../../components/ToolSection';
import type { AssignPreset } from '../homework/AssignHomeworkDrawer';
import { useRefreshClientPage } from '../clients/refreshClientPage';
import {
  SLEEP_WEEK_FIELDS,
  addDays,
  hoursMinutes,
  mondayOf,
  type DiaryNightRow,
  type SleepWeekRow,
} from './model';
import { SleepNightDrawer } from './SleepNightDrawer';
import {
  EndWindowDrawer,
  SetWindowDrawer,
  usualRuleText,
  windowText,
} from './SleepWindowDrawers';

/**
 * E45 · SleepDiaryWeek (portal) + ConfirmSleepNight (with the client) + SetSleepWindow + ClearSleepWindow + AssignHomework (sleep diary) —
 * "Client Hub — Sleep diary" (cc-health, chapter "Sleep — Sleep diary & sleep window on the client hub (portal, E45)"): the diary as the
 * client REPORTED it, with the watch's figure beside only when the client shares sleep with THIS therapist ("not shared" otherwise);
 * weekly averages and the weeks before; the window; and — FOR THE THERAPIST ONLY — what the usual sleep-restriction rule would do with
 * last week, with its basis. The therapist decides; the client never sees a suggestion.
 *
 * Rebuilt 2026-10-22 (N-4, N-9; design PORTAL-SECTIONS-2026-10-22 §2.1): results first — the Nights card, then the Sleep window card —
 * and every act is a button on the card it acts on, opening a side drawer: Enter a night (Nights), Set a new window / End the window (Sleep
 * window); ONE Assign in the header. Nothing is an open form on the page. NUMBERS, NOT VERDICTS: no "poor sleep" labels, no scores.
 */
type Props = { clientId: string; clientName: string; locale: 'en' | 'ru' };
type Tx = (key: string, options?: Record<string, unknown>) => string;
type Drawer = 'enter' | 'set' | 'end' | null;

export const ClientSleepDiaryTab = ({ clientId, clientName, locale }: Props) => {
  const t = useTranslate() as unknown as Tx;
  const { message } = AntdApp.useApp();
  const screens = Grid.useBreakpoint();
  const narrow = screens.md === false;
  const refreshClientPage = useRefreshClientPage();
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [monday, setMonday] = useState(() => mondayOf(dayOf(new Date())));
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [showEarlier, setShowEarlier] = useState(false);
  const weekQuery = useCustom<SleepWeekRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'sleepDiaryWeek',
      fields: SLEEP_WEEK_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID', value: clientId },
        week: { name: 'week', type: 'String', value: monday },
        timeZone: { name: 'timeZone', type: 'String', value: zone },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const view = weekQuery.data?.data;

  const hm = (minutes: number | null | undefined) =>
    minutes === null || minutes === undefined
      ? '–'
      : t('sleep.units.hoursMinutes', hoursMinutes(minutes));
  const mins = (m: number | null | undefined) =>
    m === null || m === undefined ? '–' : t('sleep.units.minutes', { count: m });
  const pct = (p: number | null | undefined) =>
    p === null || p === undefined ? '–' : t('sleep.units.percent', { count: p });

  const current = view?.window && !view.window.endedAt ? view.window : null;
  const lastWindow = !current ? view?.windows[0] ?? null : null;
  const earlier = (view?.windows ?? []).filter(
    (w) => w.windowId !== (current ?? lastWindow)?.windowId,
  );
  const rule = view?.usualRule;

  // "empty" covers all time: no night in this week, no week holding a filled night, no window
  const isEmpty =
    !!view &&
    view.nights.length === 0 &&
    !view.weeks.some((w) => w.averages.nightsFilled > 0) &&
    view.windows.length === 0;

  const afterSave = (toast: string) => {
    setDrawer(null);
    message.success(toast);
    refreshClientPage('assign');
    weekQuery.refetch();
  };

  const preset: AssignPreset = useMemo(
    () => ({
      kind: 'tool',
      target: 'sleepDiary',
      targets: ['sleepDiary'],
      defaults: {
        sleepDiary: {
          title: t(HOMEWORK_DEFAULT_TITLE_KEYS.sleepDiary), // Q-ru-3 A: left as it is, no title is stored
          reminderPolicy: 'dueOnly',
          deadline: {
            shape: 'repeat',
            frequency: 'daily',
            hour: 9,
            minute: 0,
            startInDays: 1,
            count: 14,
            countMax: 56,
            countUnit: 'days',
          },
        },
      },
    }),
    [t],
  );
  const assign = (primary?: boolean, label?: string) => (
    <ToolAssignButton
      clientId={clientId}
      clientName={clientName}
      preset={preset}
      primary={primary}
      label={label}
      onAssigned={() => weekQuery.refetch()}
    />
  );

  // ── the table ───────────────────────────────────────────────────────────────────────────────
  const mark = (n: DiaryNightRow) =>
    n.source === 'paper' ? (
      <Tag color="blue">
        {t('sleep.hub.fromPaper', { name: n.performerName ?? '' })}
      </Tag>
    ) : null;
  const watchText = (n: DiaryNightRow) =>
    view && !view.watchShared
      ? t('sleep.hub.watchNotShared')
      : n.watchAsleepMinutes != null
      ? hm(n.watchAsleepMinutes)
      : '–';
  const cell = {
    fellAsleep: (n: DiaryNightRow) =>
      n.nightKind === 'slept' ? mins(n.sleepOnsetMinutes) : '',
    awake: (n: DiaryNightRow) =>
      n.nightKind === 'slept' ? mins(n.wakeMinutes) : '',
    quality: (n: DiaryNightRow) =>
      n.nightKind === 'skipped' ? '' : n.quality ?? '–',
    watch: (n: DiaryNightRow) => (
      <span data-testid={`sleep-hub-night-watch-${n.night}`}>{watchText(n)}</span>
    ),
  };
  const columns = [
    {
      title: t('sleep.hub.reported'),
      render: (_: unknown, n: DiaryNightRow) => (
        <Space direction="vertical" size={0}>
          <strong>{fmtWeekday(n.night, locale)}</strong>
          {mark(n)}
        </Space>
      ),
    },
    {
      title: t('sleep.hub.inBed'),
      render: (_: unknown, n: DiaryNightRow) =>
        n.nightKind === 'skipped' ? (
          <Tag>{t('sleep.hub.skipped')}</Tag>
        ) : (
          hm(n.timeInBedMinutes)
        ),
    },
    {
      title: t('sleep.hub.asleep'),
      render: (_: unknown, n: DiaryNightRow) =>
        n.nightKind === 'skipped' ? (
          ''
        ) : (
          <span>
            {hm(n.totalSleepMinutes)}
            {n.nightKind === 'noSleep' ? (
              <Tag style={{ marginLeft: 6 }}>{t('sleep.hub.noSleep')}</Tag>
            ) : null}
          </span>
        ),
    },
    {
      title: t('sleep.hub.efficiency'),
      render: (_: unknown, n: DiaryNightRow) =>
        n.nightKind === 'skipped' ? '' : pct(n.efficiency),
    },
    {
      title: t('sleep.hub.fellAsleep'),
      responsive: ['md' as const],
      render: (_: unknown, n: DiaryNightRow) => cell.fellAsleep(n),
    },
    {
      title: t('sleep.hub.awakeInNight'),
      responsive: ['md' as const],
      render: (_: unknown, n: DiaryNightRow) => cell.awake(n),
    },
    {
      title: t('sleep.hub.quality'),
      responsive: ['md' as const],
      render: (_: unknown, n: DiaryNightRow) => cell.quality(n),
    },
    {
      title: t('sleep.hub.watch'),
      responsive: ['md' as const],
      render: (_: unknown, n: DiaryNightRow) => cell.watch(n),
    },
  ];
  // under 768 px the four secondary columns move into the row's expander
  const expandedRow = (n: DiaryNightRow) => (
    <Space direction="vertical" size={2}>
      {n.nightKind === 'slept' ? (
        <>
          <span>{`${t('sleep.hub.fellAsleep')}: ${cell.fellAsleep(n)}`}</span>
          <span>{`${t('sleep.hub.awakeInNight')}: ${cell.awake(n)}`}</span>
        </>
      ) : null}
      {n.nightKind !== 'skipped' ? (
        <span>{`${t('sleep.hub.quality')}: ${cell.quality(n)}`}</span>
      ) : null}
      <span>
        {`${t('sleep.hub.watch')}: `}
        {cell.watch(n)}
      </span>
    </Space>
  );

  // ── cards ───────────────────────────────────────────────────────────────────────────────────
  const enterButton = (
    <Button
      onClick={() => setDrawer('enter')}
      data-testid="sleep-hub-enter-open"
    >
      {t('sleepDiary.enter.open')}
    </Button>
  );

  const nightsCard = (
    <ResultCard
      testId="sleep-hub-week"
      role="result"
      title={t('sleep.hub.nights.title')}
      actions={
        <>
          <Button
            onClick={() => setMonday(addDays(monday, -7))}
            data-testid="sleep-hub-prev"
          >
            {t('sleep.hub.earlier')}
          </Button>
          <span data-testid="sleep-hub-week-label">
            {t('sleep.hub.weekOf', { date: fmtDay(monday, locale) })}
          </span>
          <Button
            onClick={() => setMonday(addDays(monday, 7))}
            data-testid="sleep-hub-next"
          >
            {t('sleep.hub.later')}
          </Button>
          {enterButton}
        </>
      }
      footer={
        view && view.weeks.length > 1 ? (
          <Space size={8} wrap data-testid="sleep-hub-weeks">
            {view.weeks
              .filter((w) => w.averages.nightsFilled > 0)
              .map((w) => (
                <Tag key={w.week}>{`${t('sleep.hub.weekOf', {
                  date: fmtDay(w.week, locale),
                })} · ${pct(w.averages.efficiency)}`}</Tag>
              ))}
          </Space>
        ) : null
      }
    >
      {view ? (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <span data-testid="sleep-hub-averages">
            {view.averages.nightsFilled > 0
              ? [
                  t('sleep.hub.nightsFilled', {
                    count: view.averages.nightsFilled,
                  }),
                  `${t('sleep.hub.inBed')} ${hm(view.averages.timeInBedMinutes)}`,
                  `${t('sleep.hub.asleep')} ${hm(view.averages.totalSleepMinutes)}`,
                  `${t('sleep.hub.efficiency')} ${pct(view.averages.efficiency)}`,
                  `${t('sleep.hub.fellAsleep')} ${mins(
                    view.averages.sleepOnsetMinutes,
                  )}`,
                  `${t('sleep.hub.awakeInNight')} ${mins(view.averages.wakeMinutes)}`,
                ].join(' · ')
              : t('sleep.hub.noNights')}
          </span>
          <Table<DiaryNightRow>
            size="small"
            pagination={false}
            rowKey="night"
            dataSource={view.nights}
            columns={columns}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: t('sleep.hub.noNights') }}
            expandable={
              narrow
                ? { expandedRowRender: expandedRow }
                : { showExpandColumn: false }
            }
            onRow={(n) =>
              ({ 'data-testid': `sleep-hub-night-${n.night}` } as never)
            }
          />
        </Space>
      ) : null}
    </ResultCard>
  );

  const windowLine = (w: NonNullable<typeof current>, prefix?: string) => (
    <span key={w.windowId} data-testid={`sleep-hub-window-${w.windowId}`}>
      {prefix ? <strong>{`${prefix} `}</strong> : null}
      {windowText(w, t, locale)}
      {w.windowId === current?.windowId && view?.upcoming ? (
        <Tag color="blue" style={{ marginLeft: 6 }}>
          {t('sleep.hub.window.upcoming')}
        </Tag>
      ) : null}
      {w.endedAt
        ? ` · ${t('sleep.hub.windowEnded', { date: fmtDay(w.endedAt, locale) })}`
        : ''}
    </span>
  );

  const windowCard = (
    <ResultCard
      testId="sleep-hub-windows"
      role="config"
      title={t('sleep.hub.window.card')}
      actions={
        <>
          <Button
            onClick={() => setDrawer('set')}
            data-testid="sleep-hub-window-set-open"
          >
            {current ? t('sleep.hub.window.setNew') : t('sleep.hub.window.setFirst')}
          </Button>
          {current ? (
            <Button
              danger
              onClick={() => setDrawer('end')}
              data-testid="sleep-hub-end-window-open"
            >
              {t('sleep.hub.window.end.title')}
            </Button>
          ) : null}
        </>
      }
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        {current ? (
          windowLine(current)
        ) : lastWindow ? (
          <>
            <Typography.Text type="secondary" data-testid="sleep-hub-no-window">
              {t('sleep.hub.noWindow')}
            </Typography.Text>
            {windowLine(lastWindow, t('sleep.hub.window.last'))}
          </>
        ) : (
          <Typography.Text type="secondary" data-testid="sleep-hub-no-window">
            {t('sleep.hub.noWindow')}
          </Typography.Text>
        )}
        {rule ? (
          <div data-testid="sleep-hub-rule">
            <strong>{`${t('sleep.hub.usualRule.forYou')} `}</strong>
            <Typography.Text data-testid="sleep-hub-rule-line">
              {usualRuleText(rule, t)}
            </Typography.Text>
          </div>
        ) : null}
        {earlier.length > 0 ? (
          <div>
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => setShowEarlier((v) => !v)}
              data-testid="sleep-hub-earlier-windows"
            >
              {t('sleep.hub.window.earlier', { count: earlier.length })}
            </Button>
            {showEarlier ? (
              <Space direction="vertical" size={2} style={{ display: 'flex' }}>
                {earlier.map((w) => windowLine(w))}
              </Space>
            ) : null}
          </div>
        ) : null}
      </Space>
    </ResultCard>
  );

  return (
    <>
      <ToolSection
        testId="sleep-hub"
        title={t('sleep.hub.title')}
        purpose={t('sleep.hub.intro')}
        actions={isEmpty ? undefined : assign()}
        loadError={weekQuery.isError ? t('sleep.hub.loadFailed') : null}
        loadErrorTestId="sleep-hub-load-error"
        loading={weekQuery.isLoading && !view}
        loadingText={t('pages.toolSection.loading')}
      >
        {isEmpty ? (
          <EmptyState
            text={t('sleep.hub.empty', { name: clientName })}
            primary={assign(true, t('sleep.hub.emptyAssign'))}
            secondary={{
              label: t('sleep.hub.emptyPaper'),
              onClick: () => setDrawer('enter'),
              testId: 'sleep-hub-empty-paper',
            }}
          />
        ) : (
          nightsCard
        )}
        {windowCard}
      </ToolSection>

      <SleepNightDrawer
        open={drawer === 'enter'}
        clientId={clientId}
        onClose={() => setDrawer(null)}
        onSaved={(night) => {
          // show the week the night is in, so the new row is on screen
          setMonday(mondayOf(night));
          afterSave(t('sleepDiary.enter.saved'));
        }}
      />
      <SetWindowDrawer
        open={drawer === 'set'}
        clientId={clientId}
        view={view}
        locale={locale}
        onClose={() => setDrawer(null)}
        onSaved={() => afterSave(t('sleep.hub.window.saved'))}
      />
      <EndWindowDrawer
        open={drawer === 'end'}
        clientId={clientId}
        window={current}
        locale={locale}
        onClose={() => setDrawer(null)}
        onSaved={() => afterSave(t('sleep.hub.window.end.done'))}
      />
    </>
  );
};
